import { macroCondition, isTesting } from '@embroider/macros';
import { registerDestructor } from '@ember/destroyable';
import { task, timeout, waitForProperty } from 'ember-concurrency';
import { tracked } from '@glimmer/tracking';
import bucketItems from './bucket-items';
import Feed from './feed';
import hasEqualUrls from 'ember-stereo/-private/utils/has-equal-urls';
import normalizeIdentifier from 'ember-stereo/-private/utils/normalize-identifier';
import { action } from '@ember/object';
import { next } from '@ember/runloop';

export default class PlaybackFeed extends Feed {
  kind = 'playback'

  @tracked episodeIndex;

  constructor(airing, options = {}) {
    super(airing, options);
    // This can either be an airing, or a now-playing object
    this.feedObject = airing;
    this.station = airing.station;
    this.broadcast = airing.broadcast;
    this.airing = airing
    this.show = airing.show;
    this.episodeIndex = options.episodeIndex ?? 0;

    this.startTask.perform()
    registerDestructor(this, this.teardown.bind(this));
  }

  get isLive() {
    return this.clock.date >= this.airing.startsAt && this.clock.date <= this.airing.endsAt
  }

  get imageUrl() {
    return this.imageSource?.medium_square?.url
  }

  get url() {
    return this.airing?.authenticatedPlaybackUrl || this.airing?.playbackUrl
  }

  get playbackState() {
    return this.airing?.playbackState
  }

  get canPlay() {
    return !!this.url
  }

  @task
  *startTask() {
    if (!this.isLoaded) {
      this.addStereoMonitors()
    }
    next(() => this.isLoaded = true)
  }

  @task({ restartable: true })
  *loadTask(trackStartTime = 0) {
    let sound;
    if (!this.loadedSound) {
      try {
        var results = yield this.stereo.loadTask.perform(this.url, {
          useConnections: ['HLS', 'NativeAudio'],
          metadata: { airing: this.airing },
          xhr: {
            manifestOnly: true,
            headers: this.session?.authenticatedHeaders,
          },
        });
      } catch (e) {
        throw new Error('failed to load sound', e);
      }
      sound = results?.sound;
    }

    yield this.setSoundPositionTask.perform(trackStartTime);
    yield this.updateNowPlayingAttributes.perform();

    this.stereo.currentSound = sound;

    yield timeout(200);
  }

  @task({ restartable: true })
  *playTask(trackStartTime = 0) {
    let sound;
    if (!this.loadedSound) {
      try {
        var results = yield this.stereo.playTask.perform((this.url), {
          useConnections: ['HLS', 'NativeAudio'],
          metadata: { airing: this.airing },
          xhr: {
            manifestOnly: true,
            headers: this.session?.authenticatedHeaders,
          },
        });
      } catch (e) {
        throw new Error('failed to load sound', e);
      }
      sound = results?.sound;
    }

    yield this.setSoundPositionTask.perform(trackStartTime);

    if (this.loadedSound) {
      if (!this.loadedSound.isPlaying && this.loadedSound) {
        yield this.loadedSound.togglePause();
      } else {
        // TODO: figure out better queuing
        // this.waitForNextPlayTask.perform(sound);
      }
    }

    // yield sound.play();
    this.updateNowPlayingAttributes.perform();
    yield timeout(200);
  }

  @task({ restartable: true })
  *setSoundPositionTask(trackStartTime) {
    let sound = this.loadedSound;

    if (
      trackStartTime &&
      typeof trackStartTime.getTime === 'function' &&
      trackStartTime.getTime()
    ) {
      var position;
      if (sound.currentTime) {
        position =
          sound.position +
          trackStartTime.getTime() -
          sound.currentTime.getTime();
      } else {
        position = trackStartTime.getTime() - this.airing.startsAt.getTime();
      }
      sound.position = position;
      yield timeout(200);
    } else if (trackStartTime && typeof trackStartTime === 'number') {
      sound.position = trackStartTime;
      yield timeout(200);
    }
  }


  @task({ drop: true, maxConcurrency: 1 })
  *updateNowPlayingAttributes() {
    yield timeout(100);

    if (!this.loadedSound) { return }

    let {
      played = [],
      unplayed = [],
      playing = {},
    } = bucketItems(this.items, this.airing, this.loadedSound);

    played.forEach((t) => {
      t.hasPlayed = true;
      t.isPlaying = false;
    });

    unplayed.forEach((t) => {
      t.isPlaying = false;
      t.hasPlayed = false;
    });

    playing.isPlaying = true;
    playing.hasPlayed = false;

    let itemType = playing?.type;
    let item = playing;

    if (itemType === 'track') {
      this.track = item;
    } else if (itemType === 'traffic') {
      this.traffic = item;
    } else if (itemType === 'note') {
      this.note = item;
    }

    this.item = item;
    this.itemType = itemType;

    yield this.feeds.updateAudioMetadata.perform(this.identifier, {
      station: this.station,
      broadcast: this.broadcast,
      sound: this.loadedSound,
      airing: this.airing,
      track: this.track,
      note: this.note,
      traffic: this.traffic,
      item: item,
      itemType
    });
  }

  @action
  onAudioUpdate({ sound }) {
    if (!sound.isDestroyed && !this.stereo.isDestroyed && hasEqualUrls(normalizeIdentifier(sound.url), normalizeIdentifier(this.loadedSound?.url))) {
      this.updateNowPlayingAttributes.perform();
    }
  }

  addStereoMonitors() {
    this.stereo.on(
      'audio-played', 
      this.onAudioUpdate.bind(this),
    );
    this.stereo.on(
      'audio-position-changed',
      this.onAudioUpdate.bind(this),
    );
    this.stereo.on(
      'audio-position-will-change',
      this.onAudioUpdate.bind(this),
    );
  }

  teardown() {
    if (!this.stereo.isDestroyed) {
      try {
        this.stereo.off(
          'audio-position-changed',
          this.onAudioPositionChanged.bind(this),
        );
        this.stereo.off(
          'audio-position-will-change',
          this.onAudioPositionChanged.bind(this),
        );
      } catch (e) {
        // no op
      }
    }
  }
}
